import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import { showApiMessage } from "../../../utils/messageHandler";
import { HostClientsConstants, Constants } from "../../../utils/constants";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../shadcn/ui/card";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../shadcn/ui/dialog";
import { LogOut, Sparkles, Gift, Building2, Bell, X, Share2 } from "lucide-react";
import toastUtils from "../../../utils/toast";
import {
  filterPackagesExcludingCurrent,
  getCurrencyCode,
  formatCurrencyDisplay,
  getMaxUsersFromUser,
  findActiveRecurringSubscription,
} from "../../../utils/subscriptionUtils";
import PricingCard from "./PricingCard";
import AddOnCard from "./AddOnCard";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import logger from "../../../utils/logger";
import { isOrgAdmin } from "../../../utils/orgAdminAccess";
import ContactAdminBanner from "../../../components/ContactAdminBanner";
import ContactAdminModal from "../../../components/ContactAdminModal";

const pricingBg = require("../../../assets/img/pricingbg.png");

const HostPricingPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currencies } = useCurrencies();
  const packagesRef = useRef(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const focusPackages = searchParams.get("view") === "packages";

  logger.log("Background image path:", pricingBg);
  const [forTeams, _setForTeams] = useState(true);
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [purchasedAddOns, setPurchasedAddOns] = useState([]);
  const [showPurchasedAddOnsModal, setShowPurchasedAddOnsModal] = useState(false);

  // Get current user
  useEffect(() => {
    const user = localService.getUser();
    setCurrentUser(user);
  }, []);

  // Check if user is platform admin - hide pricing page for them
  const isPlatformAdmin =
    currentUser?.organization?.type?.code === "PLATFORM_ADMIN" ||
    currentUser?.organization?.typeId === 1 ||
    currentUser?.organizationRole?.code === "ROLE_ADMINISTRATOR";

  // Redirect platform admins away from pricing page
  useEffect(() => {
    if (currentUser && isPlatformAdmin) {
      // Redirect to dashboard for platform admins
      history.push("/host/dashboard");
    }
  }, [currentUser, isPlatformAdmin, history]);

  // Fetch fresh subscription data
  const loadActiveSubscription = async organizationId => {
    try {
      setIsLoadingSubscription(true);
      const response = await billingService.getOrganizationSubscriptions({
        organizationId,
        limit: 100,
        orderBy: "subscriptionDate:desc",
        status: "USABLE",
      });

      const subscriptions = response?.data?.data || [];
      const latestActiveSubscription = findActiveRecurringSubscription(subscriptions);

      if (latestActiveSubscription) {
        setActiveSubscription(latestActiveSubscription);
        logger.log("Fetched active recurring subscription in pricing page:", latestActiveSubscription);
      } else {
        setActiveSubscription(null);
        logger.log("No ACTIVE recurring subscriptions found.");
      }

      // Fetch purchased add-ons (recurring = false)
      const addOnsResponse = await billingService.getOrganizationSubscriptions({
        organizationId,
        limit: 100,
        orderBy: "subscriptionDate:desc",
        status: "USABLE",
        recurring: false,
      });

      const addOnSubscriptions = addOnsResponse?.data?.data || [];
      if (addOnSubscriptions.length > 0) {
        const purchasedAddOnsList = addOnSubscriptions.map(sub => ({
          id: sub.id,
          packageId: sub.packageId || sub.package?.id,
          name: sub.package?.name || sub.servicePackage?.name || "Add-on",
          description: sub.package?.description || sub.servicePackage?.description || "",
          amount: sub.amount || sub.package?.amount || "0",
          currencyId: sub.currencyId || sub.currency || sub.package?.currencyId || 4,
          quantity: sub.quantity || 1,
          subscriptionDate: sub.subscriptionDate,
        }));
        setPurchasedAddOns(purchasedAddOnsList);
        logger.log("Fetched purchased add-ons:", purchasedAddOnsList);
      } else {
        setPurchasedAddOns([]);
      }
    } catch (error) {
      logger.error("Error loading active subscription:", error);
      setActiveSubscription(null);
      setPurchasedAddOns([]);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Fetch applicable packages based on user role
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);
        const organizationId = currentUser?.organizationId;

        // Fetch subscription and packages in parallel
        await Promise.all([
          organizationId ? loadActiveSubscription(organizationId) : Promise.resolve(),
          (async () => {
            // Determine package type based on user role
            // Host roles (who create custom packages) see STANDARD packages to subscribe to
            // Customer roles (who receive custom packages) see CUSTOM packages
            const hostRoles = HostClientsConstants.HOST_ROLES;
            const customerRoles = HostClientsConstants.HOST_CUSTOMER_ROLES;

            let _packageType = "STANDARD"; // Default
            if (customerRoles.includes(currentUser?.organizationRole?.code)) {
              _packageType = "CUSTOM"; // Customers see custom packages created by hosts
            } else if (hostRoles.includes(currentUser?.organizationRole?.code)) {
              _packageType = "STANDARD"; // Hosts see standard packages to subscribe to
            }

            // Fetch recurring packages (subscriptions)
            const recurringResponse = await billingService.getApplicablePackages({
              recurring: true,
              limit: 10,
            });

            // Fetch non-recurring packages (add-ons) using the filter Padmore added
            const addOnsResponse = await billingService.getApplicablePackages({
              recurring: false,
              limit: 10,
            });

            if (recurringResponse.data?.data) {
              logger.log("Fetched packages in pricing page:", recurringResponse.data.data);
              setPackages(recurringResponse.data.data);
            }

            if (addOnsResponse.data?.data) {
              logger.log("Fetched add-ons in pricing page:", addOnsResponse.data.data);
              setAddOns(addOnsResponse.data.data);
            }
          })(),
        ]);
      } catch (err) {
        logger.error("Error fetching data:", err);
        setError("Failed to load packages. Please try again.");
        showApiMessage(err, "load packages");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  useEffect(() => {
    if (!loading && !isLoadingSubscription && focusPackages && packagesRef.current) {
      packagesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusPackages, loading, isLoadingSubscription]);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedPkgForModal, setSelectedPkgForModal] = useState(null);
  const userIsOrgAdmin = isOrgAdmin(currentUser);

  const handleGetStarted = (e, packageId, quantity = 1) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!userIsOrgAdmin) {
      const foundPkg =
        packages.find(p => p.id === packageId) ||
        addOns.find(p => p.id === packageId);
      setSelectedPkgForModal(foundPkg || { id: packageId });
      setContactModalOpen(true);
      return;
    }

    logger.log("mdfhbbbc HostPricingPage handleGetStarted", { packageId, quantity });
    history.push(`/host/subscription-upgrade?packageId=${packageId}&quantity=${quantity}`);
  };

  // Currency display helper - uses shared utility
  const formatCurrency = (amount, currencyIdOrObject = null) => {
    const currencyCode = getCurrencyCode(currencyIdOrObject || "KES", currencies);
    return formatCurrencyDisplay(amount, currencyCode);
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Filter packages to exclude current subscription
  const filteredPackages = filterPackagesExcludingCurrent(packages, activeSubscription);
  const hasActiveSubscription = !!activeSubscription;

  const handleLogout = () => {
    try {
      // Dispatch logout action to clear Redux state
      dispatch({ type: "LOGOUT_USER" });

      // Clear user data from localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("userData");
      localStorage.removeItem("persist:root");

      // Redirect to login page
      window.location.href = "/signin";
    } catch (error) {
      logger.error("Error during logout:", error);
      showApiMessage(error, "logout");
    }
  };

  // Check for cached active subscription to show appropriate shimmer
  const cachedActiveSubscription = localService.getActiveSubscription();
  const hasCachedActiveSubscription = !!cachedActiveSubscription;

  if (loading || isLoadingSubscription) {
    return (
      <div
        className="overflow-hidden relative min-h-screen"
        style={{
          backgroundImage: `url(${pricingBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          minHeight: "130vh",
          width: "100%",
        }}
      >
        <div className="relative z-10 px-4 py-8 mx-auto max-w-7xl sm:py-12">
          {/* Header with Logout Button Shimmer */}
          <div className="flex justify-end mb-6">
            <div className="w-24 h-10 bg-white/90 rounded-md animate-pulse"></div>
          </div>

          {/* Pricing Header - Show actual headings */}
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm text-gray-700">Pricing</p>
            <h1 className="mb-4 text-4xl font-bold text-gray-900">
              {hasCachedActiveSubscription ? "Your Membership" : "Pricing on your terms"}
            </h1>
            <p className="text-base text-gray-700">
              {hasCachedActiveSubscription
                ? "Manage your current subscription and explore upgrade options"
                : "Subscribe to a package to access the platform"}
            </p>
          </div>

          {/* Current Subscription Card Shimmer - Full Width */}
          {hasCachedActiveSubscription && (
            <div className="max-w-4xl mx-auto mb-16">
              <div className="rounded-2xl p-6 shadow-sm border-2 bg-white/90 backdrop-blur-sm border-gray-200">
                <div className="space-y-6">
                  {/* Header shimmer */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-3 flex-1">
                      <div className="w-48 h-8 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-64 h-5 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                  </div>
                  {/* Stats grid shimmer */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[1, 2, 3].map(statIndex => (
                      <div key={statIndex} className="space-y-2">
                        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-28 h-3 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                  {/* Buttons shimmer */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 border-t">
                    <div className="w-48 h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="w-48 h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Packages Section - Show actual heading */}
          <div className={hasCachedActiveSubscription ? "mt-16" : ""}>
            <div className="mb-12 text-center">
              <div className="inline-flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-3xl font-bold text-gray-900">
                  {hasCachedActiveSubscription ? "Explore other plans" : "Choose your plan"}
                </h2>
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm text-gray-600">
                {hasCachedActiveSubscription
                  ? "Compare available packages and upgrade whenever you're ready."
                  : "Subscribe to a package to get started with SPACIA."}
              </p>
            </div>

            {/* Packages Grid Shimmer - Match actual grid layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {[1, 2, 3, 4].map(index => (
                <div key={index} className="space-y-6">
                  {/* Card shimmer */}
                  <div className="rounded-2xl p-6 shadow-sm border bg-white/90 backdrop-blur-sm border-gray-100">
                    <div className="space-y-4">
                      {/* Title shimmer */}
                      <div className="w-32 h-5 bg-gray-200 rounded animate-pulse"></div>
                      {/* Price shimmer */}
                      <div className="flex gap-1 items-baseline">
                        <div className="w-24 h-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                      {/* Description shimmer */}
                      <div className="space-y-2">
                        <div className="w-full h-3 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-3/4 h-3 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                      {/* Button shimmer */}
                      <div className="w-full h-10 bg-gray-200 rounded-lg animate-pulse mt-auto"></div>
                    </div>
                  </div>
                  {/* Features shimmer */}
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(featureIndex => (
                      <div key={featureIndex} className="flex gap-3 items-center">
                        <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add-ons Section - Show actual heading */}
          <div className="mt-20">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-3xl font-bold text-gray-900">Add-ons</h2>
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm text-gray-600">Enhance your subscription with additional features and services</p>
            </div>

            {/* Add-ons Grid Shimmer */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {[1, 2, 3, 4].map(index => (
                <div
                  key={index}
                  className="rounded-2xl p-6 shadow-sm border bg-white/90 backdrop-blur-sm border-purple-200 relative"
                >
                  {/* Badge shimmer */}
                  <div className="absolute -top-3 right-4 w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="space-y-4 pt-4">
                    {/* Title shimmer */}
                    <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
                    {/* Description shimmer */}
                    <div className="w-full h-4 bg-gray-200 rounded animate-pulse"></div>
                    {/* Price shimmer */}
                    <div className="flex items-baseline justify-center gap-2">
                      <div className="w-32 h-10 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    {/* Features shimmer */}
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map(featureIndex => (
                        <div key={featureIndex} className="flex gap-3 items-center">
                          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                          <div className="w-28 h-4 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                    {/* Button shimmer */}
                    <div className="w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-200 max-w-md w-full">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Unable to load packages</h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <Button onClick={() => window.location.reload()} className="w-full text-white bg-blue-600 hover:bg-blue-700">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden relative min-h-screen"
      style={{
        backgroundImage: `url(${pricingBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        minHeight: "130vh",
        width: "100%",
      }}
    >
      {/* Overlay for better text readability */}
      {/* <div className="absolute inset-0 bg-black bg-opacity-20"></div> */}

      <div className="relative z-10 px-4 py-8 mx-auto max-w-7xl sm:py-12">
        {/* Header with Logout Button and Purchased Add-ons Notification */}
        <div className="flex justify-end items-center gap-3 mb-6">
          {/* Purchased Add-ons Notification */}
          {purchasedAddOns.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowPurchasedAddOnsModal(true)}
                className="relative flex items-center justify-center w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full border border-gray-200 hover:bg-white transition-colors shadow-sm"
              >
                <Bell className="w-5 h-5 text-gray-700" />
                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-500 rounded-full">
                  {purchasedAddOns.length}
                </span>
              </button>
            </div>
          )}
          {window.location.hostname === "localhost" && (
            <Button
              variant="outline"
              onClick={() => {
                localStorage.setItem("debug_skip_subscription", "true");
                history.push("/host/spaces");
              }}
              className="flex items-center gap-2 bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100"
            >
              Skip Subscription (Debug)
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/90 backdrop-blur-sm border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {/* Pricing Header */}
        <div className="mb-12 text-center">
          <p className="mb-2 text-sm text-gray-700">Pricing</p>
          <h1 className="mb-4 text-4xl font-bold text-gray-900">
            {hasActiveSubscription ? "Your Membership" : "Pricing on your terms"}
          </h1>
          <p className="text-base text-gray-700">
            {hasActiveSubscription
              ? "Manage your current subscription and explore upgrade options"
              : "Subscribe to a package to access the platform"}
          </p>
        </div>

        {/* Current Subscription Card - Full Width */}
        {hasActiveSubscription && activeSubscription && (
          <div className="max-w-4xl mx-auto">
            <Card className="border-2" style={{ borderColor: `${Constants.HOST_EXPERIENCE_BASE_COLOR}40` }}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-3xl">{activeSubscription.servicePackage?.name}</CardTitle>
                    <CardDescription className="text-lg mt-2">
                      {activeSubscription.servicePackage?.description || "Your active subscription package"}
                    </CardDescription>
                  </div>
                  <Badge variant="default" className="bg-green-500 text-white text-sm px-3 py-1">
                    Active
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-600 mb-2">Monthly Price</p>
                    <p className="text-3xl font-bold" style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}>
                      {formatCurrency(
                        activeSubscription.amount,
                        activeSubscription.currency || activeSubscription.currencyId
                      )}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {activeSubscription.servicePackage?.recurring ? "per month" : "one-time"}
                    </p>
                  </div>
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-600 mb-2">Next Renewal</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {activeSubscription.nextRenewalDate ? formatDate(activeSubscription.nextRenewalDate) : "N/A"}
                    </p>
                    {activeSubscription.nextRenewalDate && (
                      <p className="text-sm text-gray-500 mt-1">
                        {Math.ceil((new Date(activeSubscription.nextRenewalDate) - new Date()) / (1000 * 60 * 60 * 24))}{" "}
                        days remaining
                      </p>
                    )}
                  </div>
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-600 mb-2">Team Capacity</p>
                    <p className="text-xl font-semibold text-gray-900">{getMaxUsersFromUser(currentUser)} users</p>
                    <p className="text-sm text-gray-500 mt-1">Maximum team size</p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={() => history.push("/host/billing-management")}
                      className="text-white px-8 py-6 text-base"
                      style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                    >
                      Manage Subscription
                    </Button>
                    <Button
                      onClick={() => history.push("/host/billing-management")}
                      variant="outline"
                      className="border-gray-300 px-8 py-6 text-base"
                    >
                      View Billing History
                    </Button>
                  </div>
                </div>

                <div
                  className="mt-6 p-4 rounded-lg"
                  style={{ backgroundColor: `${Constants.HOST_EXPERIENCE_BASE_COLOR}10` }}
                >
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 mt-0.5 flex-shrink-0"
                      style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-gray-700">
                      To upgrade your subscription or explore other plans, visit the{" "}
                      <button
                        onClick={() => history.push("/host/billing-management")}
                        className="font-semibold underline"
                        style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                      >
                        Billing Management
                      </button>{" "}
                      page.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Packages Section */}
        <div ref={packagesRef} className={`${hasActiveSubscription && activeSubscription ? "mt-16" : ""}`}>
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h2 className="text-3xl font-bold text-gray-900">
                {hasActiveSubscription ? "Explore other plans" : "Choose your plan"}
              </h2>
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-sm text-gray-600">
              {hasActiveSubscription
                ? "Compare available packages and upgrade whenever you’re ready."
                : "Subscribe to a package to get started with SPACIA."}
            </p>
          </div>

          {!userIsOrgAdmin && (
            <div className="max-w-4xl mx-auto mb-10">
              <ContactAdminBanner user={currentUser} />
            </div>
          )}

          {filteredPackages.length > 0 ? (
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 ${
                filteredPackages.length >= 4
                  ? "lg:grid-cols-4"
                  : filteredPackages.length === 3
                    ? "lg:grid-cols-3 lg:max-w-5xl lg:mx-auto"
                    : filteredPackages.length === 2
                      ? "lg:grid-cols-2 lg:max-w-3xl lg:mx-auto"
                      : "lg:grid-cols-1 lg:max-w-sm lg:mx-auto"
              }`}
            >
              {filteredPackages.map((pkg, index) => {
                const isEnterprise = pkg.name?.toLowerCase().includes("enterprise");

                return (
                  <PricingCard
                    key={pkg.id}
                    pkg={pkg}
                    index={index}
                    onGetStarted={handleGetStarted}
                    isEnterprise={isEnterprise}
                    currentUser={currentUser}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="mb-2 text-2xl font-semibold text-gray-900">No packages available</h3>
              <p className="mb-6 text-gray-600">
                There are currently no subscription packages available for your account.
              </p>
              <p className="text-sm text-gray-500">
                Please contact support or try logging out and back in if you believe this is an error.
              </p>
            </div>
          )}
        </div>

        {/* Add-ons Section */}
        <div className="mt-20">
          {/* Section Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h2 className="text-3xl font-bold text-gray-900">Add-ons</h2>
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-sm text-gray-600">Enhance your subscription with additional features and services</p>
          </div>

          {/* Add-ons Grid or Empty State */}
          {addOns.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {addOns.map((pkg, index) => (
                <AddOnCard
                  key={pkg.id || index}
                  pkg={pkg}
                  formatCurrency={formatCurrency}
                  onPurchase={handleGetStarted}
                  isLoading={loading}
                  currentUser={currentUser}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200">
              <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="mb-2 text-xl font-semibold text-gray-900">No add-ons available</h3>
              <p className="text-gray-600">There are currently no add-ons available for purchase.</p>
            </div>
          )}
        </div>

        {/* Role-based info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            {currentUser?.organizationRole?.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL" &&
              "Showing custom packages created by your service provider"}
            {currentUser?.organizationRole?.code === "ROLE_SUBSCRIBER_HOST" &&
              "Showing standard packages available for subscription"}
            {currentUser?.organizationRole?.code === "ROLE_ADMINISTRATOR" && "Showing all standard packages"}
          </p>
        </div>
      </div>

      {/* Purchased Add-ons Modal */}
      <Dialog open={showPurchasedAddOnsModal} onOpenChange={setShowPurchasedAddOnsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Your Purchased Add-ons</DialogTitle>
            <DialogDescription className="text-gray-600">View all your active add-on purchases</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {purchasedAddOns.length > 0 ? (
              purchasedAddOns.map((addOn, index) => (
                <Card
                  key={addOn.id || index}
                  className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold text-gray-900">{addOn.name}</CardTitle>
                        <CardDescription className="mt-1 text-gray-600">
                          {addOn.description || "Add-on feature"}
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-600 text-white flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        Active
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Price</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(addOn.amount, addOn.currencyId)}
                        </p>
                        <p className="text-xs text-gray-500">One-time payment</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Quantity</p>
                        <p className="text-lg font-semibold text-gray-900">{addOn.quantity}</p>
                        {addOn.subscriptionDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            Purchased: {new Date(addOn.subscriptionDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">No purchased add-ons found</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ContactAdminModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        user={currentUser}
        packageName={selectedPkgForModal?.name}
      />
    </div>
  );
};

export default HostPricingPage;
